import React, { useState } from "react";
import ReactDOM from "react-dom";
import { AUTH_POPUP_VARIANTS } from "../../constants/commonConstants";
import AuthModelHOC from "../../components/Reusable/AuthModelHOC";
import SignInScreen from "../pagesSpecific/Login/SignInScreen";
import ForgotPasswordScreen from "../pagesSpecific/Login/ForgotPasswordScreen";
import ResetPasswordScreen from "../pagesSpecific/Login/ResetPasswordScreen";

// Modal height map for each variant
const modalHeightMap = {
  [AUTH_POPUP_VARIANTS.SIGNIN]: "min-h-[400px]",
  [AUTH_POPUP_VARIANTS.FORGOT_PASSWORD]: "min-h-[311px]",
  [AUTH_POPUP_VARIANTS.RESET_PASSWORD]: "min-h-[400px]",
};

const AuthModelContainer = ({
  isOpen,
  onClose,
  initialVariant = AUTH_POPUP_VARIANTS.SIGNIN,
  resetToken,
}) => {
  const [variant, setVariant] = useState(initialVariant);

  // Update logic when initialVariant changes (e.g. from URL)
  // But we need to be careful not to override user manual navigation if isOpen stays open
  // For now, let's trust that if the key props change, we might want to respect that.
  // However, the original code initialized useState with initialVariant and didn't have an effect to update it.
  // We will keep it simple as per original behavior, just fixing the HOC issue.

  if (!isOpen) return null;

  // Pick the correct min-height for the modal
  const modalHeight = modalHeightMap[variant] || "min-h-[400px]";

  // Define handlers directly - they don't need to be recreated if setVariant is stable
  const handlers = {
    switchToSignIn: () => setVariant(AUTH_POPUP_VARIANTS.SIGNIN),
    switchToRegister: () => setVariant(AUTH_POPUP_VARIANTS.REGISTER),
    switchToForgotPassword: () =>
      setVariant(AUTH_POPUP_VARIANTS.FORGOT_PASSWORD),
    switchToResetPassword: () => setVariant(AUTH_POPUP_VARIANTS.RESET_PASSWORD),
  };

  // Modal content
  const renderContent = () => {
    switch (variant) {
      case AUTH_POPUP_VARIANTS.SIGNIN:
        return (
          <SignInScreen
            onClose={onClose}
            variant={variant}
            setVariant={setVariant}
            handlers={handlers}
          />
        );
      case AUTH_POPUP_VARIANTS.FORGOT_PASSWORD:
        return (
          <ForgotPasswordScreen
            onClose={onClose}
            variant={variant}
            setVariant={setVariant}
            handlers={handlers}
          />
        );
      case AUTH_POPUP_VARIANTS.RESET_PASSWORD:
        return (
          <ResetPasswordScreen
            onClose={onClose}
            variant={variant}
            setVariant={setVariant}
            handlers={handlers}
            resetToken={resetToken}
          />
        );
      default:
        // Default to SignIn if unknown variant or if Register (which might use SignInScreen or a separate one)
        // Based on original code, it only had handlers for these.
        if (Object.values(AUTH_POPUP_VARIANTS).includes(variant)) {
          // Fallback check if specific component missing for other variants
          return (
            <SignInScreen
              onClose={onClose}
              variant={variant}
              setVariant={setVariant}
              handlers={handlers}
            />
          );
        }
        return null;
    }
  };

  // Modal JSX
  const modalJSX = (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Background overlay */}
      <div
        className="fixed inset-0 bg-opacity-50 bg-black/70 backdrop-blur-[1px]"
        onClick={onClose}
      />
      {/* Modal content */}
      <div
        className={`relative z-10 max-w-md w-full ${modalHeight} pointer-events-auto`}
      >
        {renderContent()}
      </div>
    </div>
  );

  // Render modal in portal
  return ReactDOM.createPortal(modalJSX, document.body);
};

export default AuthModelContainer;
